import { PromiseUtils } from '@utils/PromiseUtils';

import _ from 'lodash';

interface IDeferredPromise<T> {
    promise: Promise<T>;
    resolve: (arg: T) => T;
    reject: (arg: string | Error) => string | Error | void;
}

type handlerType<T> = (someData: T) => Promise<T>;

export class CrossFrameUtils {
    /**
     * To be called before calling any method of this class.
     */
    public static init() {
        /**
         * postMessage handler for this frame
         */
        window.addEventListener('message', async (event: MessageEvent) => {
            if (CrossFrameUtils.isIgnorableEvent(event)) {
                console.warn('ATBase CrossFrameUtils: Event is ignorable. Check isIgnorable in CrossFrameUtils for more info', event);
                return;
            }

            const message = event.data;
            if (message.type === CrossFrameUtils.RESPONSE_TYPE) {
                CrossFrameUtils.handlePostMessageResponseEvent(event);
            } else if (message.type === CrossFrameUtils.REQUEST_TYPE) {
                try {
                    const resolved = await CrossFrameUtils.handlePostMessageRequestEvent(event);
                    return;
                } catch (rejected) {
                    console.error(`ATBase CrossFrameUtils: Request failed because ${rejected.message}`);
                }
            } else {
                const messageForLog = CrossFrameUtils.getDataForLoggingEvent(message.sensitive, message);
                console.error(`ATBase CrossFrameUtils: Received message of unknown type ${message.type}. Message body: ${messageForLog}`);
            }
        });
    }

    public static registerListener<T>(name: string, handler: handlerType<T>, isCallback?: boolean): void {
        if (isCallback === true) {
            this.callbackHandlers[name] = handler;
        } else {
            this.handlers[name] = handler;
        }
    }

    /**
     * Sends a message to other iframe. Returns a promise that resolves when this iframe
     * gets a response for the same message
     *
     * @param options
     * {
     *     frame: windowReference,
     *     message : {
     *         method: "methodName",
     *         data: "data to send"
     *         crossFrameTimeoutMillis: -1 (to execute without timeout) or value > 0 to specify timeout in milliseconds
     *         sensitive: true/false
     *         (defaults to false. tells the cross-frame framework that the message contains sensitive
     *          information that should not be logged).
     *     }
     * }
     *
     */
    public static async sendMessageToOtherFrame<T>(options): Promise<T> {
        if (!options.hasOwnProperty('frame')) {
            console.error("ATBase CrossFrameUtils: Property 'frame' is missing in options.");
        }
        const promise: Promise<T> = new Promise<T>((resolve, reject) => {
            // Attach requestId
            options.message.type = CrossFrameUtils.REQUEST_TYPE;
            options.message.id = CrossFrameUtils.requestId;
            options.message.sensitive = !!options.message.sensitive;
            CrossFrameUtils.requestId += 2;
            // save deferred
            CrossFrameUtils.requestDeferredPromises[options.message.id] = {
                promise,
                resolve,
                reject,
            };

            // send message and return promise
            options.frame.postMessage(options.message, '*');
        });
        return promise;
    }

    /**
     * Sends a response to the calling iframe
     *
     * @param event
     *              The event that this frame got, which contains the source frame reference
     * @param response
     *              The data that needs to be sent
     * @param code
     *              The response code
     */
    public static sendResponse(event, response, code: number): void {
        event.source.postMessage(
            {
                id: event.data.id,

                // set it to the same value as the incoming request message
                sensitive: !!event.data.sensitive,
                type: CrossFrameUtils.RESPONSE_TYPE,
                code,
                responseData: response,
            },
            '*',
        );
    }

    /**
     * Handles a postMessage event of RESPONSE type
     *
     * When the frame gets an event of the RESPONSE type, it finds the original request for the id
     * in the response and resolves the promise for the request with the data from the response
     *
     * @param event
     *              The event that this frame got, which contains the source frame reference
     */
    public static handlePostMessageResponseEvent(event): void {
        const message = event.data;
        const requestPromise = CrossFrameUtils.requestDeferredPromises[message.id];
        if (requestPromise) {
            if (message.code === CrossFrameUtils.CROSSFRAME_SUCCESS_CODE) {
                requestPromise.resolve(message.responseData);
            } else {
                requestPromise.reject(message.responseData);
            }
            delete CrossFrameUtils.requestDeferredPromises[message.id];
        } else {
            console.error(
                `ATBase CrossFrameUtils: Received a response event for an unknown request. Message of the response event: ${CrossFrameUtils.getDataForLoggingEvent(message.sensitive, message)}`,
            );
        }
    }

    /**
     * Handles a postMessage event of REQUEST type
     *
     * If a handler is present for the requested method, it invokes the handler
     * and sends a response back to the caller
     *
     * If a handler is absent for the requested method, it calls its parent frame
     * with the same message and sends what it's parent sends back to the caller
     *
     * If a handler is absent and it has no parent, then it sends back an error to the calling
     * frame
     * @param event
     *              The event that this frame got, which contains the source frame reference
     */
    public static async handlePostMessageRequestEvent(event) {
        const message = event.data;
        const registeredMethodHandler = message.method ? CrossFrameUtils.handlers[message.method] : undefined;
        const registeredCallbackMethodHandler = message.method ? CrossFrameUtils.callbackHandlers[message.method] : undefined;
        if (registeredMethodHandler || registeredCallbackMethodHandler) {
            let response;
            let responseCode = CrossFrameUtils.CROSSFRAME_ERROR_CODE;
            try {
                if (registeredMethodHandler) {
                    response = await registeredMethodHandler(message);
                } else if (registeredCallbackMethodHandler) {
                    response = await registeredCallbackMethodHandler(event);
                }
                responseCode = CrossFrameUtils.CROSSFRAME_SUCCESS_CODE;
                if (response) {
                    response = JSON.parse(JSON.stringify(response));
                }
            } catch (error) {
                response = error;
                const tempResponse = JSON.parse(JSON.stringify(response));
                const tempRespWithObj = JSON.parse(JSON.stringify(response, Object.getOwnPropertyNames(response)));
                // Checking if the message key is present in the object or not.
                response = tempResponse.hasOwnProperty('message') ? tempResponse : tempRespWithObj;
                responseCode = CrossFrameUtils.CROSSFRAME_ERROR_CODE;
            } finally {
                // We need to parse/stringify because objects with function fields cannot be
                // posted to frames
                CrossFrameUtils.sendResponse(event, response, responseCode);
            }
        } else {
            /**
             * If we don't have a handler, we want to send the message to the parent frame.
             *
             * If the window is it's parent window (top window) and doesn't have a handler,
             * send an error to the client
             */
            const messageForLog = CrossFrameUtils.getDataForLoggingEvent(message.sensitive, message);
            if (window.self === window.parent) {
                CrossFrameUtils.sendResponse(event, `Could not find a crossFrame handler for ${message.method}. Message: ${messageForLog}`, CrossFrameUtils.CROSSFRAME_ERROR_CODE);
                return;
            }

            /**
             * If the parent frame does not respond within the provided timeout (default 60 seconds),
             * we will fail with an error.
             *
             * Set crossFrameTimeoutMillis to -1 to execute without timeout.
             */
            const timeout = message.crossFrameTimeoutMillis || CrossFrameUtils.CROSSFRAME_RESPONSE_WAITING_TIMEOUT_MILLIS;
            try {
                const resolved = await PromiseUtils.executeWithTimeout(
                    CrossFrameUtils.sendMessageToOtherFrame({
                        frame: window.parent,
                        message: _.cloneDeep(message),
                    }),
                    {
                        durationMillis: timeout,
                        message: `Timeout for CrossFrameCall: ${message.method}. Message: ${messageForLog}`,
                    },
                );
                CrossFrameUtils.sendResponse(event, resolved, CrossFrameUtils.CROSSFRAME_SUCCESS_CODE);
            } catch (error) {
                CrossFrameUtils.sendResponse(event, error, CrossFrameUtils.CROSSFRAME_ERROR_CODE);
            }
        }
    }

    /**
     * Determines if an event should be ignored by the cross frame handlers.
     *
     * Reasons an event should be ignored is if the event does not contain the
     * expected data and type or if the event was emitted by webpack during a
     * local development build.
     *
     * @param event - the event passed to the window "message" event listener
     *
     * @returns true if the event should be ignored, false otherwise
     */
    public static isIgnorableEvent(event): boolean {
        if (_.isUndefined(event) || _.isUndefined(event.data) || _.isUndefined(event.data.type)) {
            const err = new Error();
            console.error("ATBase CrossFrameUtils: Cannot process an event that doesn't have required data", err.stack);
            return true;
        }
    }

    /**
     * Returns the JSON encoded message if sensitive is false. If it's true, it returns a
     * redacted string.
     *
     * @param {boolean} sensitive - true if the `message` should be redacted.
     * @param {object} message - the message to log or redact.
     *
     * @return {string} The JSON encoded message or the redacted string.
     */
    public static getDataForLoggingEvent(sensitive: boolean, message): string {
        if (sensitive) {
            return '<-- Redacted since sensitive=true -->';
        }
        return JSON.stringify(message);
    }

    private static readonly CROSSFRAME_RESPONSE_WAITING_TIMEOUT_MILLIS = 60000;
    private static readonly CROSSFRAME_SUCCESS_CODE = 200;
    private static readonly CROSSFRAME_ERROR_CODE = 404;
    private static readonly RESPONSE_TYPE = 'RESPONSE';
    private static readonly REQUEST_TYPE = 'REQUEST';
    // updating so that crookhook uses odd entries and JSlib uses even so as to avoid overlap.
    // issue mentioned in https://t.corp.amazon.com/P76550075/communication
    private static requestId = 0;
    private static handlers: Record<string, handlerType<any>> = {}; // Method Name - > Handler
    private static requestDeferredPromises: Record<number, IDeferredPromise<any>> = {}; // requestId -> deferred promise
    private static callbackHandlers: Record<string, handlerType<any>> = {};
}
